import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";

const STORAGE_COUNTRY = "geo_country";
const STORAGE_AUTODETECTED = "geo_lang_autodetected";
const STORAGE_USER_PICKED = "i18nextLng_userPicked";

/**
 * On first ever visit, detect the visitor's country and — only if they have
 * never manually picked a country/language — switch i18n to Spanish for
 * Mexican and Colombian visitors. The stored country also feeds the display
 * REGION (src/lib/region.ts), which drives pricing: MX ⇒ MXN, CO ⇒ COP,
 * anywhere else ⇒ USD.
 *
 * Detection sources (in order):
 *  1. Cached value in localStorage (30-day TTL)
 *  2. Edge function `detect-country` reading proxy geo headers
 *  3. Browser timezone fallback (e.g. America/Mexico_City ⇒ MX, America/Bogota ⇒ CO)
 */
export function useGeoLocale() {
  const { i18n } = useTranslation();

  useEffect(() => {
    const userPicked = localStorage.getItem(STORAGE_USER_PICKED) === "1";
    const alreadyAutodetected = localStorage.getItem(STORAGE_AUTODETECTED) === "1";
    if (userPicked || alreadyAutodetected) return;

    const apply = (country: string | null) => {
      localStorage.setItem(STORAGE_AUTODETECTED, "1");
      if (country) localStorage.setItem(STORAGE_COUNTRY, country);
      if ((country === "MX" || country === "CO") && !i18n.language?.startsWith("es")) {
        i18n.changeLanguage("es");
      } else if (country) {
        // Currency listens for this even when the language doesn't change
        // (e.g. Colombian visitor already browsing in Spanish).
        window.dispatchEvent(new Event("display-region-changed"));
      }
    };

    (async () => {
      const cached = localStorage.getItem(STORAGE_COUNTRY);
      if (cached) {
        apply(cached);
        return;
      }
      try {
        const { data } = await supabase.functions.invoke("detect-country");
        const country = (data?.country as string | null) || timezoneCountry();
        apply(country);
      } catch {
        apply(timezoneCountry());
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

function timezoneCountry(): string | null {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (/^America\/(Mexico|Tijuana|Cancun|Merida|Monterrey|Hermosillo|Mazatlan|Chihuahua|Matamoros|Ojinaga|Bahia_Banderas)/i.test(tz)) {
      return "MX";
    }
    if (/^America\/Bogota/i.test(tz)) {
      return "CO";
    }
    if (/^America\/(New_York|Chicago|Denver|Los_Angeles|Phoenix|Anchorage|Detroit|Boise|Indiana|Kentucky|Juneau|Sitka|Yakutat|Adak|Nome|Menominee|North_Dakota|Honolulu)/i.test(tz)) {
      return "US";
    }
  } catch {
    // ignore
  }
  return null;
}

/** True if the visitor's resolved country is Mexico. */
export function isMexicoVisitor(): boolean {
  return localStorage.getItem(STORAGE_COUNTRY) === "MX";
}

/** Mark that the user manually picked a language so we never auto-override. */
export function markLanguagePickedByUser() {
  localStorage.setItem(STORAGE_USER_PICKED, "1");
}
